import type { ComputedRef } from 'vue'
import { computed, watchEffect } from 'vue'
import type { Remark, Title } from '@app/application/communication/types'
import { orderBy, partition } from 'lodash'
import type { Store } from 'vuex'
import { useStore } from 'vuex'

import type {
  AccountRemarks,
  RemarksByLedger,
  ReportElementRemarks,
} from '@app/application/client_meetings/types'
import { isRemark, toTypedId } from '../utils'

interface RemarksState {
  remarks: Remark[]
}

interface StoreState {
  remarks: RemarksState
  titles: Title[]
  order: string[]
  settingOrder: boolean
  showSidebar: boolean
  insertTitlesBeforeId?: string
}

export const LAST_IN_ORDER = 'LAST_IN_ORDER'

export default function useOrderedRemarks(_store?: Store<StoreState>): {
  order: ComputedRef<string[]>
  orderedItems: ComputedRef<(Remark | Title)[]>
  orderedRemarks: ComputedRef<Remark[]>
  unorderedRemarks: ComputedRef<Remark[]>
  allRemarks: ComputedRef<Remark[]>
  remarksCount: ComputedRef<number>
  settingOrder: ComputedRef<boolean>
  showSidebar: ComputedRef<boolean>
  joinUnordered: () => Promise<void>
  joinUnorderedOpposite: () => Promise<void>
  accountRemarks: ComputedRef<AccountRemarks>
  elementRemarks: ComputedRef<ReportElementRemarks>
} {
  const store = _store || useStore<StoreState>()
  const order = computed(() => store.state.order)
  const settingOrder = computed(() => store.state.settingOrder)
  const insertTitlesBeforeId = computed(() => store.state.insertTitlesBeforeId)
  const showSidebar = computed(() => store.state.showSidebar)

  watchEffect(() => {
    if (insertTitlesBeforeId.value) {
      const newTitles = unorderedTitles.value

      const newOrderedItems = orderedItems.value
      if (insertTitlesBeforeId.value === LAST_IN_ORDER) {
        newOrderedItems.push(...newTitles)
      } else {
        const itemBeforeIndex = orderedItems.value.findIndex(
          (item) => toTypedId(item) === insertTitlesBeforeId.value
        )
        newOrderedItems.splice(itemBeforeIndex, 0, ...newTitles)
      }
      orderedItems.value = newOrderedItems

      void store.dispatch('setInsertBeforeId', null)
    }
  })

  const remarks = computed(() => {
    return orderBy(
      Object.values(store.state.remarks.remarks),
      'sortTimestamp',
      'desc'
    )
  })

  const titles = computed(() => Object.values(store.state.titles))

  const allItems = computed<(Remark | Title)[]>(() => [
    ...remarks.value,
    ...titles.value,
  ])

  const partitionItems = computed(() => {
    const [inOrder, outOrder] = partition(allItems.value, (item) =>
      order.value.includes(toTypedId(item))
    )

    inOrder.sort(
      (item, otherItem) =>
        order.value.indexOf(toTypedId(item)) -
        order.value.indexOf(toTypedId(otherItem))
    )

    // If there are no remarks in the order, we want to consider them
    // all as ordered and don't trigger any special behaviour
    if (inOrder.length > 0) {
      return [inOrder, outOrder]
    } else {
      return [outOrder, inOrder]
    }
  })

  const orderedRemarks = computed({
    get: () =>
      ([] as Remark[]).concat(partitionItems.value[0].filter(isRemark)),
    set: (newOrderedRemarks) => {
      if (settingOrder.value) {
        return
      }

      const ids = newOrderedRemarks.map((item) => toTypedId(item))
      const newIds = unorderedItems.value
        .map(toTypedId)
        .filter((id) => !ids.includes(id))
      const newOrder = [...newIds, ...ids]

      void store.dispatch('setOrder', newOrder)
    },
  })

  const unorderedRemarks = computed({
    get: () =>
      ([] as Remark[]).concat(partitionItems.value[1].filter(isRemark)),
    set: (newUnorderedRemarks) => {
      if (settingOrder.value) {
        return
      }

      const ids = newUnorderedRemarks.map((item) => toTypedId(item))
      const newIds = orderedItems.value
        .map(toTypedId)
        .filter((id) => !ids.includes(id))
      const newOrder = [...ids, ...newIds]

      void store.dispatch('setOrder', newOrder)
    },
  })

  const unorderedTitles = computed(() => {
    return ([] as Title[]).concat(
      partitionItems.value[1].filter((item): item is Title => !isRemark(item))
    )
  })

  const allRemarks = computed(() =>
    ([] as Remark[]).concat(unorderedRemarks.value, orderedRemarks.value)
  )

  const unorderedItems = computed(() => [
    ...unorderedRemarks.value,
    ...unorderedTitles.value,
  ])

  const orderedItems = computed({
    get: () => partitionItems.value[0],
    set: (newItems) => {
      if (settingOrder.value) {
        return
      }
      const ids = newItems.map((item) => toTypedId(item))
      const newOrder = []
      for (const remark of allItems.value) {
        if (!ids.includes(toTypedId(remark))) {
          newOrder.push(toTypedId(remark))
        }
      }
      newOrder.push(...ids)
      void store.dispatch('setOrder', newOrder)
    },
  })

  const remarksCount = computed(() =>
    allRemarks.value ? allRemarks.value.length : 0
  )

  function groupByLedger<K extends keyof Remark>(
    pick: (remark: Remark) => Remark[K] | undefined
  ): RemarksByLedger {
    const sets: Record<number, Set<number>> = {}
    for (const remark of allRemarks.value) {
      const id = pick(remark)
      if (typeof id !== 'number') continue
      ;(sets[remark.ledgerId] ||= new Set()).add(id)
    }
    return Object.fromEntries(
      Object.entries(sets).map(([ledgerId, ids]) => [ledgerId, [...ids]])
    )
  }

  const accountRemarks: ComputedRef<AccountRemarks> = computed(() =>
    groupByLedger(({ contextId, contextType }) =>
      contextType === 'Account' ? contextId : undefined
    )
  )

  const elementRemarks: ComputedRef<ReportElementRemarks> = computed(() =>
    groupByLedger(({ reportElementId }) => reportElementId)
  )

  async function joinUnordered(): Promise<void> {
    await store.dispatch(
      'setOrder',
      [...unorderedItems.value, ...orderedItems.value].map((item) =>
        toTypedId(item)
      )
    )
  }

  async function joinUnorderedOpposite(): Promise<void> {
    await store.dispatch(
      'setOrder',
      [...orderedItems.value, ...unorderedItems.value].map((item) =>
        toTypedId(item)
      )
    )
  }

  return {
    order,
    orderedItems,
    orderedRemarks,
    unorderedRemarks,
    allRemarks,
    remarksCount,
    settingOrder,
    showSidebar,
    joinUnordered,
    joinUnorderedOpposite,
    accountRemarks,
    elementRemarks,
  }
}
